import type {IconProps} from '@primer/octicons-react'
import type {ReactNode, SVGProps} from 'react'

const sizeMap = {small: 16, medium: 32, large: 64} as const

type CustomIconProps = Omit<SVGProps<SVGSVGElement>, 'fill' | 'children' | 'ref'> & {
  fill?: string
  size?: IconProps['size']
  verticalAlign?: IconProps['verticalAlign']
  /** Rendered as an SVG `<title>` to provide an accessible name. */
  title?: ReactNode
  /** Intrinsic width of the icon's coordinate system. Defaults to 16. */
  naturalWidth?: number
  /** Intrinsic height of the icon's coordinate system. Defaults to 16. */
  naturalHeight?: number
  children: ReactNode
}

/**
 * Renders a custom SVG icon with the same prop behavior as `@primer/octicons-react`
 * icons: `size` resolves to width/height, `fill` is applied on the `<svg>` so it
 * cascades to child paths, `verticalAlign` is applied via `style`, and `aria-label`
 * toggles `role="img"`/`aria-hidden`. Icon components compose this and provide their
 * own `<path>` children.
 */
export function CustomIcon({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  tabIndex,
  className = '',
  fill = 'currentColor',
  size = 16,
  verticalAlign = 'text-bottom',
  naturalWidth = 16,
  naturalHeight = 16,
  title,
  style,
  children,
  ...rest
}: CustomIconProps) {
  const height = typeof size === 'number' ? size : sizeMap[size]
  const width = height * (naturalWidth / naturalHeight)
  const labelled = Boolean(ariaLabel || ariaLabelledby)

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
      aria-hidden={labelled ? undefined : true}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      tabIndex={tabIndex}
      focusable={typeof tabIndex === 'number' && tabIndex >= 0 ? 'true' : 'false'}
      role={labelled ? 'img' : undefined}
      className={`octicon ${className}`.trim()}
      viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
      width={width}
      height={height}
      fill={fill}
      display="inline-block"
      overflow="visible"
      style={{verticalAlign, ...style}}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  )
}
